import { Typography, Box, Tooltip, Chip, Skeleton, Paper } from '@mui/material';
import { Role } from '../../types';
import SecurityIcon from '@mui/icons-material/Security';
import { useRef, useState, useEffect } from 'react';
import { alpha } from '@mui/material/styles';

interface RoleListProps {
  roles: Role[];
  onRoleClick: (role: Role) => void;
  loading?: boolean;
}

const chipCountSx = {
  height: 18,
  fontSize: '0.65rem',
  fontWeight: 600,
  borderRadius: '4px',
};

const contentBoxSx = {
  p: 1.5,
  bgcolor: '#ffffff',
  borderRadius: 1.5,
  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)',
  maxHeight: '110px',
  overflowY: 'auto' as const,
  '&::-webkit-scrollbar': { width: '4px' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.15)', borderRadius: '4px' },
};

const pillChipSx = {
  fontSize: '0.65rem',
  height: 22,
  borderRadius: '4px',
  backgroundColor: alpha('#0f284c', 0.06),
  color: '#0f284c',
  fontWeight: 500,
  '& .MuiChip-label': { px: 1 },
};

type TabIndex = 0 | 1 | 2 | 3; // 0: Sidebar pages, 1: Other pages, 2: Components, 3: Scopes

const tabBoxSx = (active: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  fontWeight: 500,
  fontSize: '0.75rem',
  color: active ? 'text.primary' : 'text.secondary',
  py: 0.5,
  px: 1,
  borderRadius: 1,
  cursor: 'default',
  transition: 'background-color 0.15s, color 0.15s',
  ...(active && { bgcolor: alpha('#0f284c', 0.06) }),
});

function getSidebarLabels(nav: Role['sidebar_navigation']): string[] {
  if (!nav?.length) return [];
  const out: string[] = [];
  for (const n of nav) {
    if (!n || typeof n !== 'object') continue;
    if ('items' in n && Array.isArray((n as { items: unknown[] }).items)) {
      out.push(...getSidebarLabels((n as { items: Role['sidebar_navigation'] }).items));
    } else {
      out.push((n as { label: string }).label);
    }
  }
  return out;
}

const RoleCard = ({ role, onRoleClick }: { role: Role; onRoleClick: (role: Role) => void }) => {
  const [activeTab, setActiveTab] = useState<TabIndex>(0);

  const cr = role.connected_resources || [];
  const isComponentResource = (r: unknown): boolean => {
    if (typeof r !== 'object' || r === null) return false;
    const o = r as Record<string, unknown>;
    const rawType = o.type ?? o.resourceType ?? o.resource_type;
    const typeVal = (rawType != null && rawType !== '')
      ? String(rawType).toUpperCase().trim()
      : '';
    if (typeVal === 'COMPONENT') return true;
    if (typeVal === 'PAGE' || typeVal !== '') return false;
    const path = o.path;
    return path === undefined || path === null || path === '';
  };
  const componentItems = cr.filter((r) => typeof r === 'object' && r !== null && isComponentResource(r));
  const otherPageItems = cr.filter((r) => typeof r === 'object' && r !== null && !isComponentResource(r));
  const toLabel = (r: unknown) => typeof r === 'string' ? r : (r as { name?: string; component?: string; path?: string })?.name || (r as { component?: string })?.component || (r as { path?: string })?.path || '—';
  const otherPagesLabels = otherPageItems.map(toLabel);
  const componentLabels = componentItems.map(toLabel);
  const sidebarLabels = getSidebarLabels(role.sidebar_navigation);
  const scopesCount = role.scopes?.length || 0;
  const sidebarPagesCount = sidebarLabels.length;
  const otherPagesCount = otherPageItems.length;
  const componentsCount = componentItems.length;

  return (
    <Paper
      elevation={0}
      sx={{
        cursor: 'pointer',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        minHeight: 280,
        minWidth: 500,
        width: '100%',
        maxWidth: '100%',
        borderRadius: 1.5,
        backgroundColor: 'background.paper',
        overflow: 'hidden',
        boxSizing: 'border-box',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          backgroundColor: 'primary.main',
          opacity: 0,
          transition: 'opacity 0.2s ease'
        },
        '&:hover': {
          boxShadow: '0 6px 16px rgba(15, 40, 76, 0.1)',
          transform: 'translateY(-2px)',
          '&::before': { opacity: 1 }
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
        },
      }}
      onClick={() => onRoleClick(role)}
      tabIndex={0}
      role="button"
      aria-label={`Edit role ${role.name}`}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 2.5, backgroundColor: '#0f284c', color: 'white', borderBottom: '1px solid', borderColor: alpha('#ffffff', 0.1) }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SecurityIcon fontSize="small" />
            <TruncatedText text={role.name} color="white" />
          </Box>
        </Box>

        <Box sx={{ p: 2.5, backgroundColor: alpha('#f5f8fa', 0.5) }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box onMouseEnter={() => setActiveTab(0)} sx={tabBoxSx(activeTab === 0)}>
              <span>Sidebar pages</span>
              <Chip size="small" label={sidebarPagesCount} color="primary" variant="outlined" sx={chipCountSx} />
            </Box>
            <Box onMouseEnter={() => setActiveTab(1)} sx={tabBoxSx(activeTab === 1)}>
              <span>Other pages</span>
              <Chip size="small" label={otherPagesCount} color="primary" variant="outlined" sx={chipCountSx} />
            </Box>
            <Box onMouseEnter={() => setActiveTab(2)} sx={tabBoxSx(activeTab === 2)}>
              <span>Components</span>
              <Chip size="small" label={componentsCount} color="primary" variant="outlined" sx={chipCountSx} />
            </Box>
            <Box onMouseEnter={() => setActiveTab(3)} sx={tabBoxSx(activeTab === 3)}>
              <span>Scopes</span>
              <Chip size="small" label={scopesCount} color="primary" variant="outlined" sx={chipCountSx} />
            </Box>
          </Box>

          <Box sx={contentBoxSx} onClick={(e) => e.stopPropagation()}>
            {activeTab === 0 && (
              sidebarLabels.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {sidebarLabels.map((label, index) => (
                    <Chip key={`sidebar-${index}`} label={label} size="small" sx={pillChipSx} />
                  ))}
                </Box>
              ) : (
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.disabled', fontStyle: 'italic' }}>
                  No sidebar pages
                </Typography>
              )
            )}
            {activeTab === 1 && (
              otherPagesLabels.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {otherPagesLabels.map((label, index) => (
                    <Chip key={`other-${index}`} label={label} size="small" sx={pillChipSx} />
                  ))}
                </Box>
              ) : (
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.disabled', fontStyle: 'italic' }}>
                  No other pages
                </Typography>
              )
            )}
            {activeTab === 2 && (
              componentLabels.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {componentLabels.map((label, index) => (
                    <Chip key={`component-${index}`} label={label} size="small" sx={pillChipSx} />
                  ))}
                </Box>
              ) : (
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.disabled', fontStyle: 'italic' }}>
                  No components
                </Typography>
              )
            )}
            {activeTab === 3 && (
              role.scopes && role.scopes.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {role.scopes.map((scope, index) => (
                    <Chip key={index} label={scope.name} size="small" sx={pillChipSx} />
                  ))}
                </Box>
              ) : (
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.disabled', fontStyle: 'italic' }}>
                  No specific scopes
                </Typography>
              )
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

const RoleList = ({ roles, onRoleClick, loading = false }: RoleListProps) => {
  if (loading) {
    return <RoleListSkeleton />
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(auto-fill, minmax(500px, 1fr))',
        },
        gap: '24px',
        padding: 2,
        alignContent: 'start',
      }}
    >
      {roles.map((role) => (
        <Box key={role._id} sx={{ minWidth: 0 }}>
          <RoleCard role={role} onRoleClick={onRoleClick} />
        </Box>
      ))}
    </Box>
  );
};

// Skeleton loader for the role list
const RoleListSkeleton = () => {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(auto-fill, minmax(500px, 1fr))',
        },
        gap: '24px',
        padding: 2,
        alignContent: 'start',
      }}
    >
      {[1, 2, 3, 4].map((index) => (
        <Box key={index} sx={{ minWidth: 0 }}>
        <Paper
          key={index}
          elevation={0}
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            height: '100%',
            borderRadius: 1.5,
            backgroundColor: 'background.paper',
            overflow: 'hidden',
            position: 'relative',
            boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: 'primary.main',
              opacity: 0,
            },
          }}
        >
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box 
              sx={{ 
                p: 2.5, 
                backgroundColor: '#0f284c',
                color: 'white',
                borderBottom: '1px solid',
                borderColor: alpha('#ffffff', 0.1)
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Skeleton variant="circular" width={20} height={20} sx={{ bgcolor: alpha('#ffffff', 0.1) }} />
                <Skeleton variant="text" width={140} height={24} sx={{ bgcolor: alpha('#ffffff', 0.1) }} />
              </Box>
            </Box>
            
            <Box 
              sx={{ 
                p: 2.5, 
                flexGrow: 1,
                backgroundColor: alpha('#f5f8fa', 0.5)
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                <Skeleton variant="text" width={50} height={20} />
                <Skeleton variant="rounded" width={24} height={18} sx={{ borderRadius: '4px' }} />
              </Box>
              <Skeleton 
                variant="rectangular" 
                width="100%" 
                height={110}
                sx={{ 
                  borderRadius: 1.5,
                  bgcolor: '#ffffff',
                  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)'
                }} 
              />
            </Box>
          </Box>
        </Paper>
        </Box>
      ))}
    </Box>
  );
};

// Component that only shows tooltip when text is truncated
const TruncatedText = ({ text, color = 'inherit' }: { text: string; color?: string }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      const element = textRef.current;
      if (element) {
        setIsTruncated(element.scrollWidth > element.clientWidth);
      }
    };
    
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    
    return () => {
      window.removeEventListener('resize', checkTruncation);
    };
  }, [text]);

  const typographyProps = {
    variant: "subtitle1" as const,
    component: "div" as const,
    ref: textRef,
    sx: {
      maxWidth: '210px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontWeight: 500,
      letterSpacing: '0.01em',
      color
    }
  };

  return isTruncated ? (
    <Tooltip title={text} placement="top">
      <Typography {...typographyProps}>
        {text}
      </Typography>
    </Tooltip>
  ) : (
    <Typography {...typographyProps}>
      {text}
    </Typography>
  );
};

export default RoleList;